import { AllocatedDeviceKeyResult, Button, ButtonVariants, CustomClaimValue, DeviceKey, DeviceKeys, Icons } from "@app/shared";
import { format } from "date-fns";
import { FC, useCallback, useMemo, useState } from "react";
import { downloadPdf, generateLabelPdf } from "../../../helpers/labelPdfGenerator";
import { useConfirmDialog, useFetch } from "../../../hooks";
import { DashboardLayout } from "../../layouts";
import { SortableTable, SortableTableHeaderType } from "../../shared";
import { AuthorizeView } from "../../shared/AuthorizeView";
import { Input } from "../../shared/inputs";
import { Tab, TabContainer } from "../../shared/TabContainer";

type SelectedKey = {
    id: string;
    deviceKey: string;
    source: "pending" | "allocated";
};

export const DeviceLabels: FC = () => {
    const [includePrinted, setIncludePrinted] = useState(false);
    const [pendingKeys, , pendingApi] = useFetch(() => DeviceKeys.getPending(includePrinted), [includePrinted]);
    const [selectedKeys, setSelectedKeys] = useState<Map<string, SelectedKey>>(new Map());
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<{ pending: DeviceKey[]; allocated: AllocatedDeviceKeyResult[] } | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [allocatedKeys, setAllocatedKeys] = useState<AllocatedDeviceKeyResult[] | null>(null);
    const [isLoadingAllocated, setIsLoadingAllocated] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPdfGenerating, setIsPdfGenerating] = useState(false);
    const confirmDialog = useConfirmDialog();

    const toggleSelection = useCallback((key: SelectedKey) => {
        setSelectedKeys(prev => {
            const next = new Map(prev);
            if (next.has(key.id)) {
                next.delete(key.id);
            } else {
                next.set(key.id, key);
            }
            return next;
        });
    }, []);

    const toggleAllPending = useCallback(() => {
        if (!pendingKeys) return;
        setSelectedKeys(prev => {
            const next = new Map(prev);
            const allSelected = pendingKeys.every(k => prev.has(k.id!));
            if (allSelected) {
                pendingKeys.forEach(k => next.delete(k.id!));
            } else {
                pendingKeys.forEach(k => next.set(k.id!, { id: k.id!, deviceKey: k.deviceKey, source: "pending" }));
            }
            return next;
        });
    }, [pendingKeys]);

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            await DeviceKeys.generate(60);
            pendingApi.refreshData();
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDeleteSelected = () => {
        const pendingSelected = Array.from(selectedKeys.values()).filter(k => k.source === "pending");
        if (pendingSelected.length === 0) return;

        confirmDialog.show(
            "Delete Device Keys",
            `Are you sure you want to delete ${pendingSelected.length} pending device key(s)?`,
            "Delete",
            async () => {
                for (const key of pendingSelected) {
                    await DeviceKeys.deleteKey(key.id);
                }
                setSelectedKeys(prev => {
                    const next = new Map(prev);
                    pendingSelected.forEach(k => next.delete(k.id));
                    return next;
                });
                pendingApi.refreshData();
            }
        );
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            const result = await DeviceKeys.search(searchQuery.trim());
            if (result) {
                setSearchResults({
                    pending: result.pendingKeys ?? [],
                    allocated: result.allocatedKeys ?? [],
                });
            }
        } finally {
            setIsSearching(false);
        }
    };

    const handleLoadRecentlyAllocated = async () => {
        setIsLoadingAllocated(true);
        try {
            const result = await DeviceKeys.getRecentlyAllocated(25);
            setAllocatedKeys(result ?? []);
        } finally {
            setIsLoadingAllocated(false);
        }
    };

    const handlePrintLabels = async () => {
        const keys = Array.from(selectedKeys.values());
        if (keys.length === 0) return;

        setIsPdfGenerating(true);
        try {
            const deviceKeyStrings = keys.map(k => k.deviceKey);
            const pdfBytes = await generateLabelPdf(deviceKeyStrings);
            downloadPdf(pdfBytes, `device-labels-${format(new Date(), "yyyy-MM-dd-HHmm")}.pdf`);

            const pendingIds = keys.filter(k => k.source === "pending").map(k => k.id);
            if (pendingIds.length > 0) {
                confirmDialog.show(
                    "Mark as Printed",
                    `Mark ${pendingIds.length} pending device key(s) as printed?`,
                    "Mark Printed",
                    async () => {
                        await DeviceKeys.markPrinted(pendingIds);
                        pendingApi.refreshData();
                    }
                );
            }
        } finally {
            setIsPdfGenerating(false);
        }
    };

    const allPendingSelected = useMemo(() => {
        if (!pendingKeys || pendingKeys.length === 0) return false;
        return pendingKeys.every(k => selectedKeys.has(k.id!));
    }, [pendingKeys, selectedKeys]);

    const selectedCount = selectedKeys.size;

    const pendingColumns: SortableTableHeaderType<DeviceKey>[] = [
        {
            header: (
                <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={toggleAllPending}
                    className="w-4 h-4 cursor-pointer"
                />
            ),
            width: 40,
            renderFunc: (data) => (
                <input
                    type="checkbox"
                    checked={selectedKeys.has(data?.id ?? "")}
                    className="w-4 h-4 cursor-pointer"
                />
            ),
        },
        {
            header: "Device Key",
            dataKey: "deviceKey",
            width: 150,
            renderFunc: (data) => <span className="font-mono font-semibold text-brand-primary">{data?.deviceKey}</span>,
        },
        {
            header: "Created",
            dataKey: "dateCreated",
            width: 180,
            renderFunc: (data) => data?.dateCreated ? format(data.dateCreated, "MMM d, yyyy h:mm a") : "",
        },
        {
            header: "Printed",
            dataKey: "datePrinted",
            width: 180,
            renderFunc: (data) => data?.datePrinted ? format(data.datePrinted, "MMM d, yyyy h:mm a") : <span className="text-muted-body-text">--</span>,
        },
    ];

    const allocatedColumns: SortableTableHeaderType<AllocatedDeviceKeyResult>[] = [
        {
            header: "",
            width: 40,
            renderFunc: (data) => (
                <input
                    type="checkbox"
                    checked={selectedKeys.has(data?.id ?? "")}

                    className="w-4 h-4 cursor-pointer"
                />
            ),
        },
        {
            header: "Device Key",
            dataKey: "deviceKey",
            width: 150,
            renderFunc: (data) => <span className="font-mono font-semibold">{data?.deviceKey}</span>,
        },
        {
            header: "Allocated",
            dataKey: "dateAllocated",
            width: 180,
            renderFunc: (data) => data?.dateAllocated ? format(data.dateAllocated, "MMM d, yyyy h:mm a") : "",
        },
        {
            header: "Device Name",
            dataKey: "name",
            width: 200,
        },
    ];

    const searchPendingColumns: SortableTableHeaderType<DeviceKey>[] = [
        {
            header: "",
            width: 40,
            renderFunc: (data) => (
                <input
                    type="checkbox"
                    checked={selectedKeys.has(data?.id ?? "")}
                    className="w-4 h-4 cursor-pointer"
                />
            ),
        },
        {
            header: "Device Key",
            dataKey: "deviceKey",
            width: 150,
            renderFunc: (data) => <span className="font-mono font-semibold">{data?.deviceKey}</span>,
        },
        {
            header: "Status",
            width: 100,
            renderFunc: () => <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">Pending</span>,
        },
        {
            header: "Created",
            dataKey: "dateCreated",
            width: 180,
            renderFunc: (data) => data?.dateCreated ? format(data.dateCreated, "MMM d, yyyy h:mm a") : "",
        },
    ];

    const searchAllocatedColumns: SortableTableHeaderType<AllocatedDeviceKeyResult>[] = [
        {
            header: "",
            width: 40,
            renderFunc: (data) => (
                <input
                    type="checkbox"
                    checked={selectedKeys.has(data?.id ?? "")}
                    className="w-4 h-4 cursor-pointer"
                />
            ),
        },
        {
            header: "Device Key",
            dataKey: "deviceKey",
            width: 150,
            renderFunc: (data) => <span className="font-mono font-semibold">{data?.deviceKey}</span>,
        },
        {
            header: "Status",
            width: 100,
            renderFunc: () => <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Allocated</span>,
        },
        {
            header: "Allocated",
            dataKey: "dateAllocated",
            width: 180,
            renderFunc: (data) => data?.dateAllocated ? format(data.dateAllocated, "MMM d, yyyy h:mm a") : "",
        },
        {
            header: "Device Name",
            dataKey: "name",
            width: 200,
        },
    ];

    return (
        <DashboardLayout centerChildren="Device Labels">
            <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
                {selectedCount > 0 && (
                    <div className="sticky top-0 z-10 bg-brand-primary text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
                        <span className="font-medium">{selectedCount} key{selectedCount !== 1 ? "s" : ""} selected</span>
                        <div className="flex gap-2">
                            <Button onClick={handlePrintLabels} disabled={isPdfGenerating}>
                                <Icons.Print className="inline mr-1" />
                                {isPdfGenerating ? "Generating..." : "Generate PDF"}
                            </Button>
                            <Button variant={ButtonVariants.Secondary} onClick={() => setSelectedKeys(new Map())}>
                                Clear Selection
                            </Button>
                        </div>
                    </div>
                )}

                <TabContainer>
                    <Tab title="Pending Keys">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex gap-2 items-center">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={includePrinted}
                                        onChange={(e) => setIncludePrinted(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    Show printed
                                </label>
                            </div>
                            <div className="flex gap-2 items-center">
                                <Button onClick={handleGenerate} disabled={isGenerating}>
                                    <Icons.Plus className="inline mr-1" />
                                    {isGenerating ? "Generating..." : "Generate Keys"}
                                </Button>
                                {selectedCount > 0 && (
                                    <Button variant={ButtonVariants.Destructive} onClick={handleDeleteSelected}>
                                        <Icons.Trash className="inline mr-1" />
                                        Delete Selected
                                    </Button>
                                )}
                            </div>
                        </div>
                        <SortableTable
                            columns={pendingColumns}
                            data={pendingKeys}
                            isLoading={pendingApi.isLoading}
                            emptyValue="No pending device keys. Click 'Generate Keys' to create some."
                            pagination
                            defaultPageSize={56}
                            pageSizeOptions={[28, 56, 112]}
                            initialSortKey="dateCreated"
                            initialSortDir="asc"
                            onRowClick={(row) => toggleSelection({ id: row.id!, deviceKey: row.deviceKey, source: "pending" })}
                        />
                    </Tab>

                    <Tab title="Recently Allocated">
                        <div className="flex items-center justify-end mb-3">
                            <Button onClick={handleLoadRecentlyAllocated} disabled={isLoadingAllocated}>
                                <Icons.Refresh className="inline mr-1" />
                                {isLoadingAllocated ? "Loading..." : allocatedKeys ? "Refresh" : "Load Recently Allocated"}
                            </Button>
                        </div>
                        {allocatedKeys && (
                            <SortableTable
                                columns={allocatedColumns}
                                data={allocatedKeys}
                                isLoading={isLoadingAllocated}
                                emptyValue="No recently allocated devices found."
                                pagination
                                defaultPageSize={25}
                                initialSortKey="dateAllocated"
                                initialSortDir="desc"
                                onRowClick={(row) => toggleSelection({ id: row.id!, deviceKey: row.deviceKey ?? "", source: "allocated" })}
                            />
                        )}
                        {!allocatedKeys && !isLoadingAllocated && (
                            <p className="text-muted-body-text text-sm">Click the button above to load recently allocated device keys.</p>
                        )}
                    </Tab>

                    <Tab title="Search">
                        <div className="flex gap-2 items-end mb-3">
                            <div className="flex-grow max-w-md">
                                <Input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by device key..."
                                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                />
                            </div>
                            <Button onClick={handleSearch} disabled={isSearching}>
                                <Icons.Search className="inline mr-1" />
                                {isSearching ? "Searching..." : "Search"}
                            </Button>
                            {searchResults && (
                                <Button variant={ButtonVariants.Secondary} onClick={() => setSearchResults(null)}>
                                    Clear
                                </Button>
                            )}
                        </div>
                        {searchResults && (
                            <div>
                                {searchResults.pending.length === 0 && searchResults.allocated.length === 0 && (
                                    <p className="text-muted-body-text">No results found.</p>
                                )}
                                {searchResults.pending.length > 0 && (
                                    <div className="mb-4">
                                        <h3 className="text-sm font-medium mb-1">Pending Keys ({searchResults.pending.length})</h3>
                                        <SortableTable columns={searchPendingColumns} data={searchResults.pending} emptyValue="No pending keys found" onRowClick={(row) => toggleSelection({ id: row.id!, deviceKey: row.deviceKey, source: "pending" })} />
                                    </div>
                                )}
                                {searchResults.allocated.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-medium mb-1">Allocated Keys ({searchResults.allocated.length})</h3>
                                        <SortableTable columns={searchAllocatedColumns} data={searchResults.allocated} emptyValue="No allocated keys found" onRowClick={(row) => toggleSelection({ id: row.id!, deviceKey: row.deviceKey ?? "", source: "allocated" })} />
                                    </div>
                                )}
                            </div>
                        )}
                        {!searchResults && (
                            <p className="text-muted-body-text text-sm">Enter a device key above to search across pending and allocated keys.</p>
                        )}
                    </Tab>
                </TabContainer>

                {confirmDialog.renderDialog()}
            </AuthorizeView>
        </DashboardLayout>
    );
};
